import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLE_LABELS, displayName } from '@promanage/contracts/person-display';
import { formatRef, nzDate, parseRef, portalAccessIssues } from '@promanage/core';
import { Button, Chip, Ref } from '@promanage/ui';
import { DataTable, Empty } from '../features/table/DataTable';
import { EmptyState, FilterChips, ListShell } from '../features/table/ListShell';
import { compareRows, type ColumnDef, type SortState } from '../features/table/columns';
import { PeopleNav } from '../features/people/PeopleNav';
import {
  accessLabel,
  isPortalClient,
  portalRoles,
  recordsShown,
  type PortalAccessListRow,
} from '../features/people/portal';
import { isCurrent, roleTone } from '../features/people/detail';
import { todayIso } from '../features/property/today';
import { usePortfolioScope } from '../features/portfolio/scope';
import { trpc, type RouterOutputs } from '../lib/trpc';
import { dayOf } from '../lib/when';

export const Route = createFileRoute('/people/portal')({
  component: PortalAccessPage,
});

type Row = PortalAccessListRow;
type ColumnId = 'name' | 'email' | 'clientOf' | 'access' | 'shows' | 'since';
type Filter = 'clients' | 'enabled' | 'off' | 'revoked' | 'all';
type Outcome = RouterOutputs['portalAccess']['setAccess'] & { status: 'enabled' | 'revoked' };

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'clients', label: 'Clients' },
  { value: 'enabled', label: 'Has access' },
  { value: 'off', label: 'No access' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'all', label: 'Everyone' },
];

const NO_ROWS: Row[] = [];
const DEFAULT_SORT: SortState<ColumnId> = { column: 'name', direction: 'asc' };

/**
 * Client portal access across the book — `/people/portal`.
 *
 * ONE ROW PER PERSON, because access is one switch per person. What each of them would see is not
 * chosen here: it is derived from their roles, and the "Shows" column is how many records that
 * comes to today. A role kept out of somebody's portal is set on their record's Portal tab, where
 * the roles are listed one by one.
 *
 * "CLIENTS" BY DEFAULT — tenants, owners, trustees and the rest with a role that is running or about
 * to start. That is who a portal is for; a contractor's office manager or a tenant from 2019 is
 * one filter away, not in the way.
 *
 * BULK, AND PARTIAL. Ticking forty owners and switching access on switches on everybody it can and
 * names the rest with the reason — usually a missing email address, since that is the login.
 * Refusing all forty for three would send the operator away to fix three records and back to tick
 * thirty-seven again.
 */
function PortalAccessPage() {
  const queryClient = useQueryClient();
  const { scope } = usePortfolioScope();
  const list = useQuery(
    trpc.portalAccess.list.queryOptions(
      { scope },
      { meta: { errorTitle: 'Could not load client portal access' }, staleTime: 10_000 },
    ),
  );
  const people = list.data ?? NO_ROWS;
  const today = todayIso();

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('clients');
  const [sort, setSort] = useState<SortState<ColumnId>>(DEFAULT_SORT);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const columns = useMemo(() => portalColumns(today), [today]);

  const setAccess = useMutation(
    trpc.portalAccess.setAccess.mutationOptions({
      meta: { errorTitle: 'Could not change portal access' },
      onSuccess(result, variables) {
        setOutcome({ ...result, status: variables.status });
        // Keep only the people it could not change ticked, so fixing them and trying again is
        // one click rather than a second search.
        setSelected(new Set(result.refused.map((refusal) => refusal.personId)));
        void queryClient.invalidateQueries({ queryKey: trpc.portalAccess.list.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.portalAccess.forPerson.queryKey() });
      },
    }),
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people
      .filter((row) => matchesFilter(row, filter, today))
      .filter((row) => {
        if (!q) return true;
        const asRef = parseRef(q);
        if (asRef) return asRef.entity === 'person' && asRef.sequence === row.refSeq;
        return [
          displayName(row),
          `${row.firstName} ${row.lastName}`,
          formatRef('person', row.refSeq),
          row.email,
          ...row.roles.map((role) => role.subjectLabel),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => compareRows(a, b, sort, columns));
  }, [people, filter, query, sort, today, columns]);

  // Act on what is ticked AND still on screen. A row ticked and then filtered away is one the
  // operator can no longer see, and switching it on would be a decision they did not watch made.
  const visibleIds = new Set(rows.map((row) => row.id));
  const chosen = [...selected].filter((id) => visibleIds.has(id));
  const chosenRows = rows.filter((row) => selected.has(row.id));
  const canEnable = chosenRows.filter(
    (row) => row.access?.status !== 'enabled' && portalAccessIssues(row).length === 0,
  ).length;
  const canRevoke = chosenRows.filter((row) => row.access?.status === 'enabled').length;

  const enabledCount = people.filter((row) => row.access?.status === 'enabled').length;
  const summary = list.isPending
    ? 'Loading…'
    : `${enabledCount} of ${people.length} people have portal access`;

  const act = (status: 'enabled' | 'revoked') => {
    setOutcome(null);
    setAccess.mutate({ personIds: chosen, status });
  };

  return (
    <ListShell
      title="Client portal"
      subtitle={summary}
      nav={<PeopleNav />}
      actions={
        chosen.length > 0 ? (
          <>
            <span className="text-[0.8125rem] text-ink-muted tabular-nums">
              {chosen.length} selected
            </span>
            <Button
              size="sm"
              variant="secondary"
              disabled={setAccess.isPending || canRevoke === 0}
              onClick={() => act('revoked')}
            >
              Revoke access{canRevoke > 0 ? ` (${canRevoke})` : ''}
            </Button>
            <Button
              size="sm"
              disabled={setAccess.isPending || canEnable === 0}
              onClick={() => act('enabled')}
            >
              {setAccess.isPending
                ? 'Saving'
                : `Give portal access${canEnable > 0 ? ` (${canEnable})` : ''}`}
            </Button>
          </>
        ) : null
      }
      search={query}
      onSearchChange={setQuery}
      searchPlaceholder="Search name, ref (PE000003), email or property"
      searchLabel="Search people"
      filters={
        <FilterChips
          label="Filter by access"
          value={filter}
          options={FILTERS}
          onChange={setFilter}
        />
      }
      isPending={list.isPending}
      error={list.error}
      onRetry={() => void list.refetch()}
      isEmpty={rows.length === 0}
      empty={
        <EmptyState title={query ? `No one matches “${query}”` : emptyTitle(filter)}>
          {query
            ? 'Try a different name, ref, email or property address.'
            : 'A portal is for tenants and owners. Attach a person to a tenancy or an owner and they appear here.'}
        </EmptyState>
      }
    >
      {outcome ? <OutcomeNote outcome={outcome} onDismiss={() => setOutcome(null)} /> : null}
      <DataTable
        rows={rows}
        columns={columns}
        sort={sort}
        onSortChange={setSort}
        rowKey={(row) => row.id}
        selection={{ selected, onChange: setSelected, rowLabel: (row) => displayName(row) }}
        caption={`Client portal access, one row per person. ${summary}.`}
        minWidth="56rem"
      />
    </ListShell>
  );
}

function matchesFilter(row: Row, filter: Filter, today: string): boolean {
  switch (filter) {
    case 'clients':
      return row.status === 'active' && isPortalClient(row, today);
    case 'enabled':
      return row.access?.status === 'enabled';
    case 'off':
      return row.status === 'active' && row.access === null && isPortalClient(row, today);
    case 'revoked':
      return row.access?.status === 'revoked';
    case 'all':
      return true;
  }
}

function emptyTitle(filter: Filter): string {
  switch (filter) {
    case 'enabled':
      return 'Nobody has portal access yet';
    case 'off':
      return 'Every client has portal access';
    case 'revoked':
      return 'No access has been revoked';
    default:
      return 'No clients yet';
  }
}

/**
 * What the last bulk action did. Data about the selection on screen — who changed, who could not
 * and why — so it sits above the table, not in the error notices: three owners with no email
 * address are a to-do list, not a failure.
 */
function OutcomeNote({ outcome, onDismiss }: { outcome: Outcome; onDismiss: () => void }) {
  const verb = outcome.status === 'enabled' ? 'given access' : 'revoked';
  return (
    <div
      role="status"
      className="mb-3 flex flex-col gap-1.5 rounded-md bg-surface-sunk px-4 py-3 text-[0.8125rem] ring-1 ring-rule"
    >
      <div className="flex items-start justify-between gap-3">
        <p>
          <strong className="font-semibold">
            {outcome.changed.length === 1 ? '1 person' : `${outcome.changed.length} people`} {verb}.
          </strong>{' '}
          {outcome.unchanged.length > 0
            ? `${outcome.unchanged.length} already ${outcome.status === 'enabled' ? 'had access' : 'had none'}.`
            : null}
        </p>
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
      {outcome.refused.length > 0 ? (
        <div className="text-warn">
          <p className="font-semibold">
            {outcome.refused.length === 1
              ? '1 could not be given access, and is still ticked:'
              : `${outcome.refused.length} could not be given access, and are still ticked:`}
          </p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {outcome.refused.map((refusal) => (
              <li key={refusal.personId}>
                {refusal.name} — {refusal.issues.join('; ')}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function portalColumns(today: string): ColumnDef<ColumnId, Row>[] {
  return [
    {
      id: 'name',
      label: 'Name',
      width: '22%',
      fixed: true,
      defaultVisible: true,
      sortValue: (row) => `${row.lastName} ${row.firstName}`.toLowerCase(),
      render: (row) => (
        <div className="min-w-0">
          <Link
            to="/people/$ref"
            params={{ ref: formatRef('person', row.refSeq) }}
            search={{ tab: 'portal' }}
            className="block truncate font-semibold hover:text-accent hover:underline"
          >
            {displayName(row)}
          </Link>
          <Ref entity="person" sequence={row.refSeq} />
        </div>
      ),
    },
    {
      id: 'email',
      label: 'Login email',
      width: '22%',
      defaultVisible: true,
      sortValue: (row) => row.email?.toLowerCase() ?? null,
      render: (row) =>
        row.email ? (
          <span className="block truncate">{row.email}</span>
        ) : (
          <Empty label="No email" />
        ),
    },
    {
      id: 'clientOf',
      label: 'Client of',
      width: '24%',
      defaultVisible: true,
      sortValue: (row) => currentClientRoles(row, today)[0]?.subjectLabel.toLowerCase() ?? null,
      render: (row) => {
        const [first, ...others] = currentClientRoles(row, today);
        if (!first) return <Empty label="No current role" />;
        return (
          <div className="flex min-w-0 items-center gap-2">
            <Chip tone={roleTone(first.roleType)}>{ROLE_LABELS[first.roleType]}</Chip>
            <span className="min-w-0 truncate">{first.subjectLabel}</span>
            {others.length > 0 ? (
              <span className="shrink-0 text-[0.75rem] text-ink-muted">+{others.length}</span>
            ) : null}
          </div>
        );
      },
    },
    {
      id: 'access',
      label: 'Access',
      width: '12%',
      defaultVisible: true,
      sortValue: (row) => accessLabel(row.access).label,
      render: (row) => {
        const chip = accessLabel(row.access);
        return <Chip tone={chip.tone}>{chip.label}</Chip>;
      },
    },
    {
      id: 'shows',
      label: 'Shows',
      width: '9%',
      align: 'right',
      defaultVisible: true,
      sortValue: (row) => recordsShown(row.roles, today),
      render: (row) => {
        const count = recordsShown(row.roles, today);
        return (
          <span className="tabular-nums">
            {count === 0 ? (
              <Empty label="Nothing" />
            ) : count === 1 ? (
              '1 record'
            ) : (
              `${count} records`
            )}
          </span>
        );
      },
    },
    {
      id: 'since',
      label: 'Since',
      width: '11%',
      defaultVisible: true,
      sortValue: (row) =>
        row.access?.status === 'enabled' ? nzDate(new Date(row.access.enabledAt)) : null,
      render: (row) =>
        row.access?.status === 'enabled' ? (
          <span className="tabular-nums">{dayOf(row.access.enabledAt)}</span>
        ) : (
          <Empty />
        ),
    },
  ];
}

/** Tenancy and owner roles that are running today, primary contacts first. */
function currentClientRoles(row: Row, today: string) {
  return portalRoles(row.roles)
    .filter((role) => isCurrent(role, today))
    .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
}
